"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import {
  useEffect,
  useOptimistic,
  useRef,
  useState,
  useTransition,
} from "react";

type Filters = {
  q: string;
  country: string;
  degreeType: string;
  hasFunding: boolean;
  hasResearch: boolean;
  sort: string;
};

type Props = Filters & {
  /** `label` is resolved on the server so both renders agree — Intl.DisplayNames
   * does not return identical strings in every Node/browser ICU build. */
  countries: { country: string; label: string; count: number }[];
  degreeTypes: { degreeType: string; count: number }[];
  /** True when at least one filter narrows the list — gates the Clear link. */
  filtered: boolean;
};

const count = (n: number) => n.toLocaleString("en-US");

const CONTROL =
  "min-h-11 rounded-md border border-rule bg-paper-3 px-3 text-sm text-ink " +
  "placeholder:text-muted disabled:opacity-60 " +
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus";

/** The filter state that lives in the URL, as a query string. */
function toQuery(f: Filters): string {
  const params = new URLSearchParams();
  if (f.q) params.set("q", f.q);
  if (f.country) params.set("country", f.country);
  if (f.degreeType) params.set("degreeType", f.degreeType);
  if (f.hasFunding) params.set("hasFunding", "true");
  if (f.hasResearch) params.set("hasResearch", "true");
  if (f.sort && f.sort !== "name") params.set("sort", f.sort);
  return params.toString();
}

/**
 * The only client island on the page. It is a real GET form pointed at
 * /universities/, so every control still works with JavaScript off; the
 * handlers below only upgrade that submit into a client-side navigation.
 *
 * The URL is the single source of truth. `useOptimistic` shows the new
 * selection for the frame or two a navigation takes and then defers back to
 * the URL, so a control can never drift out of step with the results below.
 */
export default function SearchControls({
  countries,
  degreeTypes,
  filtered,
  ...urlFilters
}: Props) {
  const router = useRouter();
  const [pending, startTransition] = useTransition();
  const [shown, showOptimistic] = useOptimistic(urlFilters);

  // The one control that cannot read straight from the URL: it has to stay
  // typeable between navigations. `applied` records the last value we sent, so
  // our own navigation never re-seeds the box mid-keystroke.
  const [text, setText] = useState(urlFilters.q);
  const applied = useRef(urlFilters.q);

  function go(next: Filters) {
    const query = toQuery(next);
    startTransition(() => {
      showOptimistic(next);
      router.replace(`/universities/${query ? `?${query}` : ""}`, {
        scroll: false,
      });
    });
  }

  /** Selects and toggles apply immediately, carrying the typed text along. */
  function change(patch: Partial<Filters>) {
    applied.current = text;
    go({ ...shown, ...patch, q: text });
  }

  // A URL change we did not cause — back, forward, Clear filters — re-seeds it.
  useEffect(() => {
    if (urlFilters.q !== applied.current) {
      applied.current = urlFilters.q;
      setText(urlFilters.q);
    }
  }, [urlFilters.q]);

  // Typing is debounced so a fast typist makes one request, not eight.
  useEffect(() => {
    if (text === applied.current) return;
    const timer = setTimeout(() => {
      applied.current = text;
      go({ ...urlFilters, q: text });
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  return (
    <form
      action="/universities/"
      method="get"
      onSubmit={(event) => {
        event.preventDefault();
        applied.current = text;
        go({ ...urlFilters, q: text });
      }}
      className="flex flex-col gap-3 border-y border-rule py-4"
    >
      <div className="flex max-w-2xl gap-2">
        <input
          type="search"
          name="q"
          value={text}
          onChange={(event) => setText(event.target.value)}
          placeholder="Search by university name"
          aria-label="Search by university name"
          className={`${CONTROL} min-w-0 flex-1`}
        />
        <button
          type="submit"
          className="min-h-11 shrink-0 rounded-md bg-accent px-4 text-sm font-semibold whitespace-nowrap text-accent-ink transition-[color,background-color,border-color] duration-200 ease-[var(--ease-out-expo)] hover:bg-accent-hover active:bg-accent-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
        >
          Search
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <select
          name="country"
          value={shown.country}
          onChange={(event) => change({ country: event.target.value })}
          aria-label="Filter by country"
          className={`${CONTROL} max-w-full`}
        >
          <option value="">All countries</option>
          {countries.map((row) => (
            <option key={row.country} value={row.country}>
              {row.label} ({count(row.count)})
            </option>
          ))}
        </select>

        <select
          name="degreeType"
          value={shown.degreeType}
          onChange={(event) => change({ degreeType: event.target.value })}
          aria-label="Filter by degree type"
          className={`${CONTROL} max-w-full`}
        >
          <option value="">Any degree type</option>
          {degreeTypes.map((row) => (
            <option key={row.degreeType} value={row.degreeType}>
              {row.degreeType} ({count(row.count)})
            </option>
          ))}
        </select>

        <select
          name="sort"
          value={shown.sort}
          onChange={(event) => change({ sort: event.target.value })}
          aria-label="Sort results"
          className={`${CONTROL} max-w-full`}
        >
          <option value="name">A to Z</option>
          <option value="programs">Most programs</option>
          <option value="research">Most cited research</option>
        </select>

        <Toggle
          name="hasFunding"
          label="Has funding"
          checked={shown.hasFunding}
          onChange={(value) => change({ hasFunding: value })}
        />
        <Toggle
          name="hasResearch"
          label="Has research"
          checked={shown.hasResearch}
          onChange={(value) => change({ hasResearch: value })}
        />

        {filtered && (
          <Link
            href="/universities/"
            className="min-h-11 content-center px-1 text-sm whitespace-nowrap text-muted underline-offset-2 transition-[color,background-color,border-color] duration-200 ease-[var(--ease-out-expo)] hover:text-ink hover:underline active:text-accent-text focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
          >
            Clear filters
          </Link>
        )}

        <span
          role="status"
          aria-live="polite"
          className="text-sm whitespace-nowrap text-muted"
        >
          {pending ? "Updating…" : ""}
        </span>
      </div>
    </form>
  );
}

function Toggle({
  name,
  label,
  checked,
  onChange,
}: {
  name: string;
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="cursor-pointer">
      <input
        type="checkbox"
        name={name}
        value="true"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="peer sr-only"
      />
      <span className="flex min-h-11 items-center rounded-md border border-rule px-3 text-sm whitespace-nowrap text-ink-2 transition-[color,background-color,border-color] duration-200 ease-[var(--ease-out-expo)] peer-checked:border-accent peer-checked:text-accent-text peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-focus hover:text-ink active:border-accent">
        {label}
      </span>
    </label>
  );
}
